import { THEMES } from '@dfl/mui-react-common';
import { createTheme } from '@mui/material';
import { grey } from '@mui/material/colors';
import { buttonsTheme } from './buttons.theme';
import { cardsTheme } from './cards.theme';
import { chipsTheme } from './chips.theme';
import { dialogsTheme } from './dialogs.theme';
import { inputsTheme } from './inputs.theme';
import { papersTheme } from './papers.theme';
import { tableTheme } from './table.theme';
import { tabsTheme } from './tabs.theme';
import { typographiesTheme as typography } from './typographies.theme';

export const RED = '#d32f2f';
export const GREEN = '#4caf50';
export const WARNING = '#FFAE34';

declare module '@mui/material/styles' {
  interface Palette {
    neutral: Palette['primary'];
    tertiary: Palette['primary'];
  }

  interface PaletteOptions {
    neutral?: PaletteOptions['primary'];
    tertiary?: PaletteOptions['primary'];
  }
}

export const common = createTheme({
  components: {
    ...buttonsTheme,
    ...cardsTheme,
    ...chipsTheme,
    ...dialogsTheme,
    ...inputsTheme,
    ...papersTheme,
    ...tableTheme,
    ...tabsTheme,
    MuiCssBaseline: {
      styleOverrides: (theme) => ({
        ':root, :before, :after': {
          '--primary-color': `${theme.palette.primary.main} !important`,
        },
        '.truncate': {
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          whiteSpace: 'nowrap',
        },
      }),
    },
  },
  typography,
});

const LIGHT = {
  ...common,
  palette: {
    mode: THEMES.LIGHT,
    sidebar: {
      background: 'primary.main',
      color: '#fff',
      activeColor: '#fff',
      active: 'primary.light',
    },
    spaceSelector: '#f3f4f9',
    primary: {
      main: '#0D2D49',
      dark: '#17185B',
      light: '#6BB1E4',
      contrastText: '#fff',
    },
    secondary: {
      ...grey,
      dark: grey['900'],
      main: grey['700'],
      light: grey['500'],
      contrastText: '#f5f5f5',
    },
    neutral: {
      main: '#213053',
      light: '#3C4E76',
      dark: '#553089',
    },
    tertiary: {
      main: '#fff',
      contrastText: '#7AB97E',
    },
    background: {
      default: '#f5f5f5',
      paper: '#f5f5f5',
    },
    formLabel: '#000000de',
    link: '#6BB1E4',
    success: {
      main: '#47CE81',
      light: '#EAFAF1',
    },
    warning: {
      main: '#FFAE34',
      light: '#FFF3E0',
    },
  },
};

const DARK = {
  ...common,
  palette: {
    mode: THEMES.LIGHT,
    sidebar: {
      background: 'neutral.light',
      color: '#f3f4f9',
      activeColor: 'primary.main',
      active: 'neutral.main',
    },
    spaceSelector: '#f3f4f9',
    primary: {
      main: '#0D2D49',
      dark: '#17185B',
      light: '#6BB1E4',
    },
    secondary: {
      ...grey,
      dark: grey['900'],
      main: grey['700'],
      light: grey['500'],
      contrastText: '#213053',
    },
    neutral: {
      main: '#213053',
      light: '#3C4E76',
      dark: '#553089',
    },
    tertiary: {
      main: '#3C4E76',
      light: '#f3f4f9',
      contrastText: '#3C4E76',
    },
    background: {
      default: '#213053',
      paper: '#3C4E76',
    },
    formLabel: '#000000de',
    link: '#1a0dab',
    success: {
      main: '#47CE81',
      light: '#EAFAF1',
    },
    warning: {
      main: '#FFAE34',
      light: '#FFF3E0',
    },
    error: {
      main: '#2979ff',
      light: '#e1f5fe',
    },
  },
};

export const THEME_SETTING = {
  [THEMES.LIGHT]: LIGHT,
  [THEMES.DARK]: DARK,
};
